import "./message-users.scss";

import { autoinject, bindable, bindingMode, computedFrom, customElement, observable } from "aurelia-framework";

import nameof from "../../../common/nameof";
import { IMessageUser, INewMessage } from "../../../interfaces/i-message";
import { ITypeaheadOptions } from "../../../interfaces/i-typeahead";
import { IGetCurrentUserResult, IGetUsersLeanResult } from "../../../interfaces/i-user";
import { MessageService } from "../../../services/message-service";
import { ToastrService } from "../../../services/toastr-service";
import { UsersService } from "../../../services/users-service";
import { AssetsService } from "./../../../services/assets-service";
import { PermissionActionEnum } from "../../../enums/permission-action-enum";
import { AdministrativePermissionEnum } from "../../../enums/administrative-permission-enum";
import { PermissionManager } from "../../../common/utilities/permission-manager";
import { CurrentUserService } from "../../../services/current-user-service";
import { ICreateResponse } from "../../../interfaces/i-response";
import { PatientsService } from "../../../services/patient-service";
import { PatientMessage } from "../../../models/patient/patient-message";
import moment from "moment";

@autoinject
@customElement("message-users")
export class MessageUsers {
    @bindable({ defaultBindingMode: bindingMode.toView })
    public showCancel: boolean = true;
    @bindable({ defaultBindingMode: bindingMode.toView })
    public newMessage: INewMessage = {
        title: "",
        message: "",
        userIds: [],
        isImportant: false
    };
    @bindable({ defaultBindingMode: bindingMode.toView })
    public users: IMessageUser[] = [];
    @bindable({ defaultBindingMode: bindingMode.toView })
    public showInput: boolean = false;
    @bindable({ defaultBindingMode: bindingMode.fromView })
    public closeSendMessage: (args: { isCancel: boolean }) => void;
    @observable({ changeHandler: nameof<MessageUsers>("tagInputUsersChanged") })
    public tagInputUsers: ITypeaheadOptions[] = [];
    @bindable({ defaultBindingMode: bindingMode.toView })
    public isPatientMessage: boolean = false;
    @bindable({ defaultBindingMode: bindingMode.toView })
    public patientId: string = "";
    @bindable({ defaultBindingMode: bindingMode.toView })
    public newPatientMessage: PatientMessage = new PatientMessage();
    @bindable({ defaultBindingMode: bindingMode.twoWay })
    public newMessageId: string = "";
    @bindable({ defaultBindingMode: bindingMode.toView })
    public isNoteAlreadySaved: boolean = false;
    public files: File[] = [];
    private _permissionManager: PermissionManager;
    private _toastrService: ToastrService;
    private _messageService: MessageService;
    private _usersService: UsersService;
    private _currentUserService: CurrentUserService;
    private _assetsService: AssetsService;
    private _patientsService: PatientsService;
    public isSendingMessage: boolean = false;
    public currentUser: IGetCurrentUserResult;

    @computedFrom(`${nameof<MessageUsers>("newMessage")}.${nameof<INewMessage>("message")}`)
    public get initMessage() {
        return this.newMessage.message.length > 0 ? this.newMessage.message : "";
    }

    public constructor(
        assetsService: AssetsService,
        toastrService: ToastrService,
        messageService: MessageService,
        usersService: UsersService,
        currentUserService: CurrentUserService,
        permissionManager: PermissionManager,
        patientsService: PatientsService
    ) {
        this._assetsService = assetsService;
        this._toastrService = toastrService;
        this._messageService = messageService;
        this._usersService = usersService;
        this._currentUserService = currentUserService;
        this._permissionManager = permissionManager;
        this._patientsService = patientsService;
    }

    public bind() {
        this.checkPermission();
    }

    public async attached() {
        // this.canShow = !!this.users && this.users.length > 0;
        this.currentUser = await this._currentUserService.getUser();
    }

    private checkPermission() {
        let hasMessageCenterPermission = this._permissionManager.checkPermission([
            {
                resource: AdministrativePermissionEnum.MessageCenter,
                action: PermissionActionEnum.View
            }
        ]);
        if (!hasMessageCenterPermission) {
            this._toastrService.error({
                title: `Error`,
                message: `You do not have permission to view this data. Contact an administrator at your agency to request access.`
            });

            this.closeSendMessage({ isCancel: true });
        }
    }

    public async getUsersList(filter: string, limit: number) {
        return this.getDisplayUsers(await this._usersService.getUsersLean({ term: filter, page: 1, pageLength: 10 }));
    }

    public getDisplayUsers(data: IGetUsersLeanResult) {
        if (data) {
            let filteredItems = data.items.filter((user) => user.id !== this.currentUser.id);
            return filteredItems.map((user) => ({
                name: `${user.lastName ? user.lastName + ", " : ""}${user.firstName}`,
                value: user.id,
                firstName: user.firstName,
                lastName: user.lastName
            }));
        }
        return [];
    }

    public tagInputUsersChanged() {
        if (this.newMessage) {
            this.newMessage.userIds = this.tagInputUsers.map((user) => user.value);
        }
    }

    public async sendMessage(message: string) {
        if (this.isPatientMessage && this.tagInputUsers.length === 0) {
            this._toastrService.error({
                title: "Error",
                message: `Please select a recipient.`
            });
            return;
        }
        if (this.newMessage) {
            try {
                let messageDate = "";
                this.isSendingMessage = true;
                this.newMessage.message = message;
                if (this.files?.length > 25) {
                    this.files.splice(25);
                }
                this.newMessage.assetIds = await this._assetsService.uploadMessageFiles(this.files);
                if (!this.showInput) {
                    this.newMessage.userIds = this.users.map((user) => user.userId);
                }

                if (this.isNoteAlreadySaved) {
                    messageDate = this.newPatientMessage.messageDate;
                } else {
                    let isoDateAndTime = new Date().toISOString();
                    messageDate = moment(isoDateAndTime).format("MM/DD/YYYY HH:mm");
                }

                if (this.isPatientMessage) {
                    let patientMessage = Object.assign(
                        { ...this.newPatientMessage, messageDate },
                        {
                            recipientIds: this.newMessage.userIds,
                            sendToMessageCenter: true,
                            message: this.newMessage.message,
                            assetId: this.newMessage.assetIds?.[0] ?? ""
                        }
                    );
                    let createNewMessageResponse: ICreateResponse = await this._patientsService.createPatientMessage(
                        this.patientId,
                        patientMessage
                    );
                    this.newMessageId = createNewMessageResponse.id;
                } else {
                    await this._messageService.newConversation(this.newMessage);
                }
                this.closeSendMessage({ isCancel: false });
                this._toastrService.success({
                    title: "Message Sent",
                    message: `Your message has been successfully sent.`
                });
            } catch (e) {
                console.error(e);
                this._toastrService.error({
                    title: "Message Send Error",
                    message: `There was a problem while sending the message. Please try again.`
                });
            } finally {
                this.isSendingMessage = false;
                this.files = [];
                this.tagInputUsers = [];
                this.newPatientMessage.message = "";
            }
        }
    }

    public cancelSendMessage() {
        this.closeSendMessage({ isCancel: true });
        this.tagInputUsers = [];
    }

    public navigateToUser(user: IMessageUser) {
        let win = window.open(`/employees/employee-center/${user.userId}`, "_blank");
        win.focus();
    }

    public toggleImportant() {
        this.newMessage.isImportant = !this.newMessage.isImportant;
    }
}
